import "./Paragraph.scss";
import { useEffect, useState } from "react";
import {
  Row,
  Input,
  DatePicker,
  InputNumber,
} from "antd";
import parse from 'html-react-parser';

import moment from "moment";
import { useTranslation } from "react-i18next";
import { validateEntityRutHelper, validateCompanyRutHelper, validateRutHelper } from "../../../../../helpers";

const Paragraph = ({ component, record, mode, lang, handleChangeValues }) => {
  const { t } = useTranslation('forms')
  const [ changes, setChanges ] = useState(false)
  const [ hasErrors, setHasErrors ] = useState(false)
  const [ fieldErrors, setFieldErrors ] = useState(null);

  const validateField = (field) => {
    field.error = false
    if (field.required && (field.value === null || field.value === undefined || field.value === '')) {
      field.error = true
      return t('forms.errors.requiredFields');
    }
    if(field.value) {
      if (field.validation?.maxLength && field.value.length > field.validation.maxLength) {
        field.error = true
        return t('forms.errors.maxLength', { max: field.validation.maxLength });
      }
      if (field.validation?.type === 'number' || field.validation?.type === 'percent') {
        const n = Number(field.value)
        if(isNaN(n)) {
          field.error = true
          return t('forms.errors.number');
        }else {
          if (field.validation.type === 'percent' && (n < 0 || n > 100)) {
            field.error = true
            return t('forms.errors.percent');
          }
          const decimals = field.validation?.decimals??0
          const maxLength = field.validation?.maxLength??10
          const pattern = `^\\d{1,${maxLength-decimals}}([.,]\\d{0,${decimals}})?$`;
          if(!(new RegExp(pattern)).test(field.value)) {
            field.error = true
            return t('forms.errors.number');
          }
        }
      }else if (field.validation?.type === 'email') {
        if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value)) {
          field.error = true
          return t('forms.errors.email');
        }
      }else if (field.validation?.type === 'rut') {
        if(!validateEntityRutHelper(field.value)) {
          field.error = true
          return t('forms.validation.rut_invalid');
        }
      }else if (field.validation?.type === 'rutNat') {
        if(!validateRutHelper(field.value)) {
          field.error = true
          return t('forms.validation.rut_persona_invalid');
        }
      }else if (field.validation?.type === 'rutEmp') {
        if(!validateCompanyRutHelper(field.value)) {
          field.error = true
          return t('forms.validation.rut_empresa_invalid');
        }
      }
    }
    
    return null;
  };

  const validateAllFields = () => {
    let errors = {};
    if (component.fieldSet && component.fieldSet.fields) {
      component.fieldSet.fields.forEach(field => {
        const error = validateField(field);
        if (error) errors[field.key] = error;
      });
    }
    setFieldErrors(errors);
    setHasErrors(Object.keys(errors).length > 0);
  };

  useEffect(() => {
    validateAllFields();
  }, [])

  const handleChangeFieldValue = (field, value) => {
    field.value = value
    if(handleChangeValues) {
      handleChangeValues(component.fieldSet)
      validateAllFields();
      setChanges(true)
    }
  }

  const handleReadOnly = (field,readOnly)=>{
    if(handleChangeValues) {
      field.readOnly = readOnly
      handleChangeValues(component, false)
    }
  }

  const getStepNumber = (field) => {
    if(field.validation?.decimals && field.validation?.decimals > 0) {
      return 1 / Math.pow(10, field.validation?.decimals)
    }
    return 1
  }

  const getField = (field) => {
    field.show = true
    if(mode === 'pdf') {
      return <span className="paragraph-input-value-field">{field.value}</span>
    }else {
      if(field.typeField === 'INPUT') {
        // Si el tipo de validación es number o percent, usar InputNumber
        if(field.validation?.type === 'number' || field.validation?.type === 'percent') {
          return <InputNumber size="small"
            className={'field-paragraph'+ (field.error ? ' error-field-paragraph' : '')}
            placeholder={field.title && field.title[lang]}
            defaultValue={ field.value }
            min={field.validation?.type === 'percent' ? 0 : undefined}
            max={field.validation?.type === 'percent' ? 100 : undefined}
            step={getStepNumber(field)}
            maxLength={field.validation?.maxLength ? field.validation.maxLength+1 : 150}
            onFocus= {(e)=>handleReadOnly(field,false)}
            onBlur= {(e)=>handleReadOnly(field,true)}
            readOnly = {mode === 'pdf' || field.readOnly === true}
            onChange={(value) => handleChangeFieldValue(field, value)}
          />
        }
        // Si no, usar Input normal
        return <Input size="small" className={'field-paragraph'+(field.error ? ' error-field-paragraph' : '')} 
          placeholder={field.title && field.title[lang]} 
          defaultValue={ field.value }
          autoComplete="off"
          maxLength={field.validation?.maxLength ? field.validation.maxLength+1 : 150}
          onFocus= {(e)=>handleReadOnly(field,false)}
          onBlur= {(e)=>handleReadOnly(field,true)}
          readOnly = {mode === 'pdf' || field.readOnly === true}
          onChange={(e) => handleChangeFieldValue(field, e.target.value)} />
      }else {
        return <DatePicker size="small" className={'field-paragraph'+ (field.error ? ' error-field-paragraph' : '')}
        format="DD/MM/YYYY"
        placeholder={field.title && field.title[lang]}
        defaultValue={field.value && moment(field.value,"DD/MM/YYYY")}
        onChange={(momentObj) => handleChangeFieldValue(field, momentObj ? moment(momentObj).format( "DD/MM/YYYY" ) : null ) } />
      }
    }
  }

  const getText = (text) => {
    if (!text) return null;
    const transformed = text.replace(/\{\{(\d+|rut|nombre|name|nome|email|empresa|company)\}\}/g, (_, match) => `<span data-var="${match}">{{${match}}}</span>`);

    const html = parse(transformed, {
      replace: (node) => {
        if (node.name === "span" && node.attribs?.["data-var"]) {
          const data = node.attribs["data-var"];
          if(data === "rut") {
            return <span className={"paragraph-input-value-field " + mode}>{record ? record.rut : "«Rut»" }</span>
          }else if(data === "nombre" || data === "name" || data === "nome") {
            return <span className={"paragraph-input-value-field " + mode}>{record ? record.nombre : "«" + data + "»"}</span>
          }else if(data === "email") {
            return <span className={"paragraph-input-value-field " + mode}>{record ? record.email : "«" + data + "»"}</span>
          }else if((data === "empresa" || data === "company")) {
            return <span className={"paragraph-input-value-field " + mode}>{record ? record.subcliente?.name : "«" + data + "»"}</span>
          }else {
            const index = parseInt(data, 10) - 1;
            const field = component?.fieldSet?.fields?.[index];
            if (field) return getField(field);
          }
        }
      }
    });
    return html
  };

  return (
    <div className="form-manager-paragraph-form">
      { mode !== 'pdf' && changes && hasErrors && fieldErrors && (
        <Row className="has-errors-fieldset" style={{margin:'6px 0px'}}>
            <div style={{ color: 'red', fontSize: '13px', margin:4}}>{Object.values(fieldErrors)[0]}</div>
        </Row>
      )}
      <Row className="paragraph-text">
        { component.text && getText(component.text[lang]) }
      </Row>
      
    </div>
  )
}

export default Paragraph;
